<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import { useAuthStore } from '@/stores/useAuthStore'

const { t, locale } = useI18n()
const { umsokn } = useUmsokn()
const auth = useAuthStore()

const props = defineProps<{
  disable?: boolean
  handleNext?: () => void
  handlePrev?: () => void
}>()

/** Legacy lists kept verbatim, switch by language (isl vs en) */
const hlutfallSelectEN = ['3.5', '3.0', '2.5', '2.0', '1.5', '1.0', '0.5']
const hlutfallSelectIS = ['3,5', '3,0', '2,5', '2,0', '1,5', '1,0', '0,5']

// Determine "Icelandic" like legacy `lang === 'isl'`
// Prefer i18n locale, but fall back to stored value for compatibility.
const isIsl = computed(() => {
  const stored = (typeof window !== 'undefined' && localStorage.getItem('lang')) || ''
  const loc = locale.value?.toString().toLowerCase() || ''
  return stored.toLowerCase() === 'isl' || loc.startsWith('is')
})

const hlutfallItems = computed(() => (isIsl.value ? hlutfallSelectIS : hlutfallSelectEN))

// --- Vuelidate rules ---
const rules = {
  umsokn: {
    hlutfall: { required },
  },
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  },
})
</script>

<template>
  <div
    v-if="umsokn"
  >
    <v-row>
      <v-col>
        <h2 class="form__header">
          {{ t('umsokn_tilgreind_sereign_step2_header') }}
        </h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-select
          v-model="umsokn.hlutfall"
          :items="hlutfallItems"
          :label="t('umsokn_tilgreind_sereign_step2_hlutfall')"
          :readonly="props.disable"
          :menu-props="{ contentClass: 'custom__select-light' }"
          variant="outlined"
          density="comfortable"
          :error="v$.umsokn.hlutfall.$error"
          :error-messages="
            v$.umsokn.hlutfall.$error
              ? [t('umsokn_tilgreind_sereign_step2_hlutfall_error')]
              : []
          "
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <p style="font-size: 14px;">
          {{ t('umsokn_tilgreind_sereign_step2_disclaimer') }}
        </p>
      <div class="col-sm-12 col-md-12">
        <p
          style="font-size: 14px;"
          v-html="t('umsokn_tilgreind_sereign_step2_disclaimer2')"
        ></p>
      </div>
      </v-col>
    </v-row>
  </div>
</template>

<style lang="scss" scoped>
input {
  width: 100%;
}
.forms__fadein {
  opacity: 0;
  -moz-transition: opacity 0.4s ease-in-out;
  -o-transition: opacity 0.4s ease-in-out;
  -webkit-transition: opacity 0.4s ease-in-out;
  transition: opacity 0.4s ease-in-out;
}
.forms__fadeout {
  opacity: 1;
}
.slideup,
.slidedown {
  max-height: 0;
  overflow: hidden;
  width: 100%;
  -webkit-transition: max-height 0.8s ease-in-out;
  -moz-transition: max-height 0.8s ease-in-out;
  -o-transition: max-height 0.8s ease-in-out;
  transition: max-height 0.8s ease-in-out;
}

.slidedown {
  max-height: 500px;
}
</style>
